import type { Booklet } from './types';

/** Set a "days.3.body" style path on a copy of the booklet. */
export function setPath(booklet: Booklet, path: string, value: unknown): Booklet {
  const next = structuredClone(booklet) as unknown as Record<string, unknown>;
  const keys = path.split('.');
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const child = cur[keys[i]];
    if (child == null || typeof child !== 'object') throw new Error(`Cannot edit ${path}`);
    cur = child as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return next as unknown as Booklet;
}

export function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, key) => (cur == null ? undefined : (cur as Record<string, unknown>)[key]), obj);
}

const SECTION_NAMES: Record<string, string> = {
  tour: 'Tour info',
  route: 'Route',
  schedule: 'Schedule',
  days: 'Day',
  experience: 'Experience',
  packages: 'Package',
  bikes: 'Bike',
  addons: 'Add-on',
  included: 'Included list',
  notIncluded: 'Not-included list',
  notes: 'Footnotes',
  exclusions: 'Exclusions',
  deposits: 'Deposit',
  contact: 'Contact',
  covers: 'Full-page photo',
  hiddenPages: 'Pages shown',
  itinerary: 'Itinerary',
  useTemplatePhotos: 'Sample photos',
  unmapped: 'Unmapped import text',
  status: 'Status',
  name: 'Booklet name',
  template: 'Template',
  layout: 'Layout',
  photos: 'Photos',
};

const FIELD_NAMES: Record<string, string> = {
  title: 'title', body: 'description', eyebrow: 'eyebrow', tagline: 'tagline', coverHeadline: 'cover headline',
  durationDays: 'duration', season: 'season', year: 'year', currency: 'currency', difficulty: 'difficulty',
  cities: 'cities', totalDistanceKm: 'total distance', map: 'map', mode: 'mode', assetId: 'photo',
  distanceKm: 'distance', label: 'label', description: 'description', icon: 'icon', photo: 'photo',
  name: 'name', price: 'price', amount: 'amount', pending: 'pending', priceLabel: 'price label',
  includes: 'includes', hotel: 'hotel', brand: 'brand', model: 'model', unit: 'unit', points: 'points',
  surcharge: 'surcharge', address: 'address', email: 'email', website: 'website', instagram: 'Instagram',
  tiktok: 'TikTok', phones: 'phones', region: 'region', number: 'number', value: 'value', of: 'out of',
  photos: 'photos', day: 'day number', destination: 'destination', date: 'date',
};

/**
 * "packages.1.price.amount" -> "Package 2 · price · amount". Day indices are shown
 * as the day number where the booklet is available.
 */
export function describePath(path: string, booklet?: Booklet): string {
  const parts = path.split('.');
  const [section, ...rest] = parts;
  const out: string[] = [SECTION_NAMES[section] ?? section];
  rest.forEach((part, i) => {
    if (/^\d+$/.test(part) && i === 0) {
      const index = Number(part);
      if (section === 'days' && booklet?.days[index]) out[0] = `Day ${booklet.days[index].day}`;
      else if (section === 'packages' && booklet?.packages[index]) out[0] = `Package ${booklet.packages[index].name}`;
      else if (section === 'experience' && booklet?.experience[index]) out[0] = `Experience "${booklet.experience[index].label}"`;
      else out[0] = `${out[0]} ${index + 1}`;
    } else if (/^\d+$/.test(part)) {
      out.push(`#${Number(part) + 1}`);
    } else {
      out.push(FIELD_NAMES[part] ?? part);
    }
  });
  return out.join(' · ');
}

export function formatValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const SYMBOLS: Record<string, { locale: string; symbol: string; after: boolean }> = {
  EUR: { locale: 'de-DE', symbol: '€', after: true },
  USD: { locale: 'en-US', symbol: '$', after: false },
  GBP: { locale: 'en-GB', symbol: '£', after: false },
  CHF: { locale: 'de-CH', symbol: 'CHF ', after: false },
  IDR: { locale: 'id-ID', symbol: 'Rp ', after: false },
};

export function money(amount: number | null | undefined, currency = 'EUR'): string {
  if (amount == null) return '—';
  const def = SYMBOLS[currency] ?? SYMBOLS.EUR;
  const n = new Intl.NumberFormat(def.locale, { maximumFractionDigits: currency === 'IDR' ? 0 : 2 }).format(amount);
  return def.after ? `${n} ${def.symbol}` : `${def.symbol}${n}`;
}
